import { Injectable, type OnDestroy } from '@angular/core';

/** What AskSimpo sends and expects back. Do not rename these. */
const NOTIFICATION = 'asksimpo_notification';
const NOTIFICATION_ACK = 'asksimpo_notification_ack';
const PERMISSION_REQUEST = 'asksimpo_notification_permission_request';
const PERMISSION_REPLY = 'asksimpo_notification_permission';

/** The row AskSimpo's API produces. Extra fields may appear; none of them are required here. */
export interface AskSimpoNotification {
  readonly id: string;
  readonly title?: string;
  readonly body?: string;
  /** 'success' | 'error' | 'info' — drives nothing here, useful if you badge your own nav. */
  readonly level?: string;
  /** 'chat.turn', 'task.image_batch', 'schedule.*' … */
  readonly kind?: string;
  /** AskSimpo-relative, e.g. '/chat/2f9c…'. */
  readonly link?: string | null;
  readonly source?: { readonly chat_id?: string } | null;
  readonly created_at?: string;
}

export type AskSimpoLinkHandler = (link: string) => void;

@Injectable({ providedIn: 'root' })
export class AskSimpoHostNotificationsService implements OnDestroy {
  /** Your own icon. A notification with no mark on it reads as something the browser produced. */
  private static readonly ICON = '/assets/images/simpo_new_logo.svg';

  private frame: Window | null = null;
  private origin = '';
  private onLink: AskSimpoLinkHandler | null = null;
  private listening = false;

  private readonly onMessage = (event: MessageEvent): void => {
    // Origin first, every time. This handler raises OS-level UI and reads arbitrary strings out
    // of the payload, so anything but the frame we attached to is ignored outright.
    if (!this.frame || event.source !== this.frame) return;
    if (this.origin && this.origin !== '*' && event.origin !== this.origin) return;

    const data = event.data as { type?: unknown; data?: unknown } | null;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;

    if (data.type === PERMISSION_REQUEST) {
      void this.answerPermission();
      return;
    }
    if (data.type === NOTIFICATION && data.data) {
      void this.raise(data.data as AskSimpoNotification);
    }
  };

  /** Start handling notifications for one embedded AskSimpo. Safe to call on every frame load. */
  attach(frame: Window, origin: string, onLink?: AskSimpoLinkHandler): void {
    this.frame = frame;
    try {
      this.origin = new URL(origin).origin;
    } catch {
      this.origin = origin ? origin.replace(/\/+$/, '') : '';
    }
    this.onLink = onLink ?? null;
    if (this.listening) return;
    window.addEventListener('message', this.onMessage);
    this.listening = true;
  }

  detach(): void {
    if (this.listening) window.removeEventListener('message', this.onMessage);
    this.listening = false;
    this.frame = null;
    this.onLink = null;
  }

  ngOnDestroy(): void {
    this.detach();
  }

  private reply(message: Record<string, unknown>): void {
    // Targeted at the exact origin rather than '*': these carry no secrets, but a wildcard here
    // would post them to whatever happens to occupy the frame after a navigation.
    this.frame?.postMessage(message, this.origin || '*');
  }

  private async answerPermission(): Promise<void> {
    if (!('Notification' in window)) {
      this.reply({ type: PERMISSION_REPLY, permission: 'unsupported' });
      return;
    }
    if (Notification.permission !== 'default') {
      this.reply({ type: PERMISSION_REPLY, permission: Notification.permission });
      return;
    }
    let permission: NotificationPermission = 'default';
    try {
      // The click that produced this happened in the iframe, so this is not a user gesture in
      // this frame. Chrome allows it and may show its quieter UI; a browser that refuses
      // outright throws, and 'default' is the honest answer — AskSimpo stops offering rather
      // than leaving a button that does nothing.
      permission = await Notification.requestPermission();
    } catch {
      permission = 'default';
    }
    this.reply({ type: PERMISSION_REPLY, permission });
  }

  private async raise(notification: AskSimpoNotification): Promise<void> {
    let shown = false;

    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const alert = new Notification(notification.title || 'Simpo.ai', {
          body: notification.body || '',
          icon: AskSimpoHostNotificationsService.ICON,
          // AskSimpo already dedupes by id; the tag stops the OS stacking a replay on top of
          // one still on screen.
          tag: notification.id,
        });
        alert.onclick = () => {
          window.focus();
          const link = notification.link;
          if (link && this.onLink) this.onLink(link);
          alert.close();
        };
        shown = true;
      } catch {
        // Some platforms (notably Android Chrome) require a service worker and throw here.
        shown = false;
      }
    }

    // Always, including on failure. Silence means "no handler" and makes AskSimpo stop trying;
    // `shown: false` means "heard you, did not raise it", and it keeps asking next time.
    this.reply({ type: NOTIFICATION_ACK, id: notification.id, shown });
  }
}
